import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container, Row } from '@narvar/nth-grid';
import uniq from 'lodash/uniq';
import styled from 'styled-components';
import CarrierPickup from '../CarrierPickup';
import StripePayment from '../StripePayment';
import LocationJS from '../LocationJS';
import ReturnOptionsCard from '../ReturnOptionsCard';
import ReturnOptionsMethods from '../ReturnOptionsMethods';
import ShippingAddressForm from '../ShippingAddressForm';
import { updateStep2Complete } from '../../actions/page';
import { selectOption, selectMethod, selectDefaultOption } from '../../actions/returnOptions';
import { formatCurrencyValue } from '../../lib/currencyFormatters';
import { scrollIntoViewSmoothly } from '../../lib/scrollIntoView';
import { MAIL, PICKUP, PREFERRED } from '../../constants/returnMethods';
import DEFAULT_COPY from '../../constants/defaultCopy';
import LearnMoreModal from '../LearnMoreModal';
import { learnMoreMethods } from '../LearnMoreModal/modalData';
import PickupLayout from '../PickupLayout';
import { getAllFees } from '../../lib/consolidatedMethods';
import PreferencesSaveState from '../PreferencesSaveState';
import { rawString } from '../Language';
import { setShippingAddressFormInvalid } from '../../actions/addresses';

export const mapStateToProps = ({
  page: { productType, languageCode, countryCode, giftOrder, locale } = {},
  language,
  returnsSettings: {
    [productType]: {
      disableGiftAddressForm = false,
      returnOptions: { availableOptionsApi, methods: settingsMethods = {} } = {},
    } = {},
  } = {},
  returnOptions: {
    selectedOptionId,
    selectedOptionMethodKey,
    returnOptions,
    returnOptionMethods,
    returnOptionVisibility,
  },
  orderInfo: { orderCurrencyCode: currencyCode } = {},
}) => ({
  showForm: Boolean(
    settingsMethods[selectedOptionMethodKey] &&
      ((giftOrder && locale === 'en_US' && !disableGiftAddressForm) ||
        (settingsMethods[selectedOptionMethodKey].get_address && selectedOptionId !== PICKUP)),
  ),
  language,
  languageCode,
  selectedOptionId,
  selectedOptionMethodKey,
  returnOptions,
  returnOptionMethods,
  returnOptionVisibility,
  locale: `${languageCode}_${countryCode}`,
  currencyCode,
  availableOptionsApi,
  giftOrder,
});

const FormContainer = styled.div`
  width: 60%;
  margin: auto;
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;

const ReturnOptions = ({
  dispatch,
  language,
  languageCode,
  selectedOptionId,
  selectedOptionMethodKey,
  returnOptionMethods,
  returnOptionVisibility,
  returnOptions,
  showForm,
  locale,
  currencyCode,
  toStep3,
  giftOrder,
}) => {
  const {
    methods = [],
    displayMethods = false,
    locations: locationBasedOption,
  } = returnOptions.find(({ id }) => id === selectedOptionId) || {};
  const returnOptionMethodsContainerRef = React.useRef(null);
  const storeLocatorContainerRef = React.useRef(null);
  const preloadOptionState = React.useRef(() => {
    if (selectedOptionId) dispatch(selectOption(selectedOptionId));
  });

  React.useEffect(preloadOptionState.current, []);

  React.useEffect(() => {
    if (!selectedOptionId && !selectedOptionMethodKey) {
      dispatch(selectDefaultOption());
    }
    if (!showForm) {
      dispatch(setShippingAddressFormInvalid(false));
    }
  }, [dispatch, selectedOptionId, selectedOptionMethodKey]);

  React.useEffect(() => {
    if (!selectedOptionId) {
      return;
    }

    const onlyOneMethodAndSubMethod =
      returnOptionVisibility &&
      returnOptionVisibility.length === 1 &&
      returnOptionVisibility[0].methods &&
      returnOptionVisibility[0].methods.length === 1;

    if (onlyOneMethodAndSubMethod || !selectedOptionId) {
      return;
    }

    scrollIntoViewSmoothly(returnOptionMethodsContainerRef.current);
  }, [returnOptionVisibility, selectedOptionId]);

  React.useEffect(() => {
    const onlyOneMethodAndSubMethod =
      returnOptionVisibility &&
      returnOptionVisibility.length === 1 &&
      returnOptionVisibility[0].methods &&
      returnOptionVisibility[0].methods.length === 1;

    if (onlyOneMethodAndSubMethod || !selectedOptionMethodKey) {
      return;
    }

    if (locationBasedOption && selectedOptionMethodKey) {
      return;
    }

    scrollIntoViewSmoothly(storeLocatorContainerRef.current);
  }, [returnOptionVisibility, locationBasedOption, selectedOptionMethodKey]);

  const handleOptionClick = (id) => () => {
    if (id === selectedOptionId) return;
    dispatch(updateStep2Complete(false));
    dispatch(selectOption(id));
  };

  const handleMethodClick = (methodKey) => () => {
    if (methodKey === selectedOptionMethodKey) return;
    dispatch(selectMethod(methodKey));
  };

  const generateFeeMsg = (option) => {
    const freeLabel = rawString({
      langKey: [`returns.return_options.${option.id}.free_label`, 'returns.general.free_label'],
    });
    const multiFeeLabel = language[`returns.return_options.${option.id}.multi_fee_label`];
    const { shippingFees, serviceFees } = getAllFees(option);

    if (uniq(shippingFees).length !== 1) return multiFeeLabel;

    const feeString =
      shippingFees[0] > 0
        ? formatCurrencyValue({ value: shippingFees[0], locale, currencyCode })
        : freeLabel;

    if (serviceFees.length === 1) {
      const serviceFeeAmount = formatCurrencyValue({
        value: serviceFees[0],
        locale,
        currencyCode,
      });
      const langCopy =
        language['returns.general.service_fee'] ||
        DEFAULT_COPY[languageCode]['returns.general.service_fee'];
      if (shippingFees[0] > 0) {
        return `${feeString} + ${serviceFeeAmount} ${langCopy}`;
      }
      return `${serviceFeeAmount} ${langCopy}`;
    } else if (serviceFees.length > 1) {
      return multiFeeLabel;
    }

    return feeString;
  };

  const { store_locator: storeLocatorEnabled } = returnOptionMethods[selectedOptionMethodKey] || {};
  const [{ id: firstType, methods: firstMethods = [] } = {}] = returnOptionVisibility;
  const hideMailSubOptions =
    (returnOptionVisibility.length === 1 && firstType === MAIL && firstMethods.length < 2) ||
    locationBasedOption;

  return (
    <Container>
      <PreferencesSaveState />
      <div
        style={{ textAlign: 'center', borderSpacing: '1rem' }}
        data-test="return-options-container"
        className="return-options-container"
      >
        {returnOptionVisibility.map((option, index) => (
          <ReturnOptionsCard
            hideLink={Boolean(option.locations)}
            onClick={handleOptionClick(option.id, index)}
            optionsCount={returnOptionVisibility.length}
            selected={option.id === selectedOptionId}
            fee={generateFeeMsg(option)}
            optionKey={option.id}
            key={option.id}
            preferred={!giftOrder && option.display_method_user_preference === PREFERRED}
            {...(learnMoreMethods.includes(option.id)
              ? {
                  modal: (
                    <LearnMoreModal onConfirm={handleOptionClick(option.id)} optionId={option.id} />
                  ),
                }
              : {})}
          />
        ))}
      </div>
      {!hideMailSubOptions && (
        <Row
          ref={returnOptionMethodsContainerRef}
          id="return-option-methods"
          style={{ margin: '0 -1.75rem' }}
        >
          <ReturnOptionsMethods
            display={displayMethods}
            onClick={handleMethodClick}
            methods={methods}
            language={language}
            optionId={selectedOptionId}
            selectedOptionMethodKey={selectedOptionMethodKey}
            locale={locale}
            currencyCode={currencyCode}
          />
        </Row>
      )}
      {(storeLocatorEnabled || locationBasedOption) && (
        <Row id="store-locator" ref={storeLocatorContainerRef}>
          <LocationJS key={locationBasedOption ? selectedOptionId : selectedOptionMethodKey} />
        </Row>
      )}
      {showForm && (
        <Row id="shipping-address-form">
          <FormContainer>
            <ShippingAddressForm />
          </FormContainer>
        </Row>
      )}
      <CarrierPickup />
      <PickupLayout />
      <StripePayment onSuccess={toStep3} />
    </Container>
  );
};

ReturnOptions.defaultProps = {
  currencyCode: undefined,
  availableOptionsApi: undefined,
  giftOrder: false,
};

ReturnOptions.propTypes = {
  dispatch: PropTypes.func.isRequired,
  language: PropTypes.object.isRequired,
  languageCode: PropTypes.string.isRequired,
  selectedOptionId: PropTypes.string.isRequired,
  selectedOptionMethodKey: PropTypes.string.isRequired,
  returnOptionMethods: PropTypes.object.isRequired,
  returnOptionVisibility: PropTypes.array.isRequired,
  returnOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      methods: PropTypes.arrayOf(
        PropTypes.shape({
          return_method: PropTypes.string,
          carrier: PropTypes.string,
          return_cost: PropTypes.shape({
            return_shipping_fee: PropTypes.number.isRequired,
          }),
        }),
      ),
    }),
  ).isRequired,
  showForm: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  currencyCode: PropTypes.string,
  toStep3: PropTypes.func.isRequired,
  availableOptionsApi: PropTypes.bool,
  giftOrder: PropTypes.bool,
};

export default connect(mapStateToProps)(ReturnOptions);
